import React, { useEffect } from 'react';
import { useLoaderData, useNavigation, redirect } from 'react-router-dom';
import {
  VaLoadingIndicator,
  VaBreadcrumbs,
  VaLinkAction,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { Toggler } from '~/platform/utilities/feature-toggles';
import recordEvent from 'platform/monitoring/record-event';
import api from '../utilities/api';
import {
  SEARCH_PARAMS,
  SORT_BY,
  SUBMISSION_DEFAULTS,
  SORT_DEFAULTS,
  submissionsBC,
  SUBMISSIONS_BC_LABEL,
} from '../utilities/constants';
import SortForm from '../components/SortForm';
import Pagination from '../components/Pagination';
import PaginationMeta from '../components/PaginationMeta';
import SubmissionsPageResults from '../components/SubmissionsPageResults';
import { QUICK_SUBMIT_BASE, withBasename } from '../utilities/quickSubmit';

const SubmissionsPage = title => {
  useEffect(() => {
    document.title = title.title;
  }, [title]);
  const submissions = useLoaderData().data || [];
  const meta = useLoaderData().meta || {};
  const navigation = useNavigation();
  return (
    <section className="poa-request submissions">
      <VaBreadcrumbs
        breadcrumbList={submissionsBC}
        label={SUBMISSIONS_BC_LABEL}
        homeVeteransAffairs={false}
        wrapping
      />
      <h1
        data-testid="submissions-header"
        className="submissions__search-header"
      >
        Submissions
      </h1>
      <p className="submissions-subtext__copy">
        Start here to submit VA forms for your claimants.
      </p>
      <Toggler
        toggleName={
          Toggler.TOGGLE_NAMES.accreditedRepresentativePortalSubmit686cV2
        }
      >
        <Toggler.Disabled>
          <div className="submissions__form-start">
            <h2 className="submissions__form-name vads-u-font-size--h3">
              VA Form 21-686c
            </h2>
            <p className="submissions__form-description vads-u-font-size--h4">
              Application Request to Add and/or Remove Dependents
            </p>
            <p className="submissions__subtext submissions__subtext">
              The form will be processed by VA Centralized Mail after
              submission.
            </p>
            <va-link-action
              href="/representative/representative-form-upload/submit-va-form-21-686c"
              text="Upload and submit VA Form 21-686c"
              type="secondary"
            />
          </div>
        </Toggler.Disabled>
      </Toggler>

      <div className="submissions__form-start">
        <h2 className="submissions__form-name vads-u-font-size--h3 submissions__margin-top">
          VA Form 21-526EZ
        </h2>
        <p className="submissions__form-description vads-u-font-size--h4">
          Application for Disability Compensation and Related Compensation
          Benefits
        </p>
        <p className="submissions__subtext submissions__subtext">
          The form will be processed by VA Centralized Mail after submission.
        </p>
        <va-link-action
          href="/representative/representative-form-upload/submit-va-form-21-526EZ"
          text="Upload and submit VA Form 21-526EZ"
          type="secondary"
        />
      </div>
      <div className="submissions__form-start">
        <h2 className="submissions__form-name vads-u-font-size--h3 submissions__margin-top">
          VA Form 21-0966
        </h2>
        <p className="submissions__form-description vads-u-font-size--h4">
          Intent to File a Claim for Compensation and/or Pension, or Survivors
          Pension and/or DIC
        </p>
        <p className="submissions__subtext submissions__subtext">
          The intent to file will be recorded immediately after submission.
        </p>
        <va-link-action
          href="/representative/representative-form-upload/submit-va-form-21-0966/introduction"
          text="Submit online VA Form 21-0966"
          type="secondary"
        />
      </div>
      <Toggler
        toggleName={
          Toggler.TOGGLE_NAMES.accreditedRepresentativePortalSubmit686cV2
        }
      >
        <Toggler.Enabled>
          <div className="submissions__form-start">
            <h2 className="submissions__form-name vads-u-font-size--h3 submissions__margin-top">
              VA Form 21-686c (online)
            </h2>
            <p className="submissions__form-description vads-u-font-size--h4">
              Add or remove a dependent using an interview style form
            </p>
            <p className="submissions__subtext submissions__subtext">
              You’ll confirm you represent the claimant before starting the
              form.
            </p>
            <va-link-action
              href="/representative/representative-form-upload/va-form-21-686c-674/introduction"
              text="File VA Form 21-686c"
              type="secondary"
            />
          </div>
        </Toggler.Enabled>
      </Toggler>
      <Toggler
        toggleName={
          Toggler.TOGGLE_NAMES.accreditedRepresentativePortalCdUpdateQsLink
        }
      >
        <Toggler.Enabled>
          <div className="submissions__form-start">
            <h2 className="submissions__form-name vads-u-font-size--h3 submissions__margin-top">
              Submit any file with QuickSubmit
            </h2>
            <p className="submissions__form-description vads-u-font-size--h4">
              Fill out and submit an eForm PDF. Or upload existing PDFs,
              supporting evidence, or multiple files to submit as a packet.
            </p>
            <VaLinkAction
              href={withBasename(QUICK_SUBMIT_BASE)}
              text="Go to QuickSubmit"
              type="secondary"
            />
          </div>
        </Toggler.Enabled>
      </Toggler>
      <Toggler
        toggleName={
          Toggler.TOGGLE_NAMES.accreditedRepresentativePortalSubmitAnyFile
        }
      >
        <Toggler.Enabled>
          <div className="submissions__form-start">
            <h2 className="submissions__form-name vads-u-font-size--h3 submissions__margin-top">
              Submit other forms
            </h2>
            <p className="submissions__form-description vads-u-font-size--h4">
              Upload and submit a completed VA form and any supporting documents
            </p>
            <p className="submissions__subtext submissions__subtext">
              The form will be processed by VA Centralized Mail after
              submission.
            </p>
            <VaLinkAction
              href="/representative/representative-form-upload/submit-other-forms/introduction"
              text="Upload and submit a form"
              type="secondary"
              onClick={() =>
                recordEvent({
                  event: 'nav-link-click',
                  'link-label': 'Upload and submit a form',
                  'link-destination':
                    '/representative/representative-form-upload/submit-other-forms/introduction',
                  'link-origin': undefined,
                })
              }
            />
          </div>
        </Toggler.Enabled>
      </Toggler>
      <hr aria-hidden="true" />
      <h2 className="submissions__search-header">Recent submissions</h2>
      <p className="submissions-subtext__copy--secondary">
        This list shows only your submissions sent through this portal from the
        past 60 days.
      </p>
      <SortForm
        options={[
          {
            sort: 'newest',
            label: 'Newest',
          },
          {
            sort: 'oldest',
            label: 'Oldest',
          },
        ]}
        defaults={SORT_DEFAULTS}
      />
      {meta && submissions ? (
        <PaginationMeta
          meta={meta}
          results={submissions}
          resultType="submissions"
          defaults={SORT_DEFAULTS}
        />
      ) : (
        ''
      )}
      <div className="submissions-page-table-container">
        {navigation.state === 'loading' ? (
          <VaLoadingIndicator message="Loading..." />
        ) : (
          <SubmissionsPageResults
            submissions={submissions}
            context="SubmissionsPage"
          />
        )}
        <Pagination meta={meta} defaults={SUBMISSION_DEFAULTS} />
      </div>
    </section>
  );
};

SubmissionsPage.loader = async ({ request }) => {
  const { searchParams } = new URL(request.url);
  const sort = searchParams.get(SEARCH_PARAMS.SORT);
  const size = searchParams.get(SEARCH_PARAMS.SIZE);
  const number = searchParams.get(SEARCH_PARAMS.NUMBER);
  if (!Object.values(SORT_BY).includes(sort)) {
    searchParams.set(SEARCH_PARAMS.SORT, SORT_BY.NEWEST);
    searchParams.set(SEARCH_PARAMS.SIZE, SUBMISSION_DEFAULTS.SIZE);
    searchParams.set(SEARCH_PARAMS.NUMBER, SUBMISSION_DEFAULTS.NUMBER);
    throw redirect(`?${searchParams}`);
  }

  // Wait for the Promise-based Response object
  const response = await api.getSubmissions(
    { sort, size, number },
    {
      signal: request.signal,
    },
  );

  // Returns the actual response so we can grab the `.submission` and `.meta` with the `useLoaderData()` hook
  return response?.json();
};

export default SubmissionsPage;
